/**
 * Sentry утилита для логирования ошибок
 */
import * as Sentry from '@sentry/vue';

/**
 * Фильтрует чувствительные данные из объекта
 */
export function sanitizeData(data: unknown): unknown {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }

  const sensitiveKeys = [
    'password',
    'token',
    'authorization',
    'cookie',
    'api_key',
    'apiKey',
    'secret',
    'access_token',
    'refresh_token',
    'bearer',
  ];

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Фильтрует чувствительные заголовки
 */
export function sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
  const sensitiveHeaders = [
    'authorization',
    'cookie',
    'x-api-key',
    'x-auth-token',
  ];

  const sanitized: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveHeaders.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Отправляет HTTP ошибку в Sentry
 */
export function sendErrorToSentry(
  error: {
    status?: number;
    message?: string;
    code?: string;
    data?: unknown;
  },
  url: string,
  method: string,
  requestBody?: string | unknown,
  requestHeaders?: Record<string, string>,
  responseData?: unknown
): void {
  try {
    const status = error.status || 0;
    const isServerError = status >= 400;
    const isNetworkError = status === 0 || (error.message && error.message.includes('Network'));

    // Отправляем только 4xx, 5xx и network errors
    if (!isServerError && !isNetworkError) {
      return;
    }

    // Теги для фильтрации в Sentry
    const tags = {
      api_endpoint: url,
      http_status: status > 0 ? String(status) : 'network_error',
      http_method: method,
    };

    // Дополнительные данные
    const extra: Record<string, unknown> = {
      error_message: error.message || 'Unknown error',
      error_code: error.code || String(status) || 'network_error',
    };

    // Добавляем данные запроса (без чувствительных данных)
    if (requestBody) {
      try {
        const parsedBody = typeof requestBody === 'string'
          ? JSON.parse(requestBody)
          : requestBody;
        extra.request_data = sanitizeData(parsedBody);
      } catch {
        extra.request_body = '[Non-JSON body]';
      }
    }

    // Добавляем заголовки запроса (без чувствительных данных)
    if (requestHeaders) {
      extra.request_headers = sanitizeHeaders(requestHeaders);
    }

    // Добавляем данные ответа
    if (responseData) {
      extra.response_data = sanitizeData(responseData);
    } else if (error.data) {
      extra.response_data = sanitizeData(error.data);
    }

    // Создаем Error объект для Sentry
    const sentryError = new Error(error.message || `HTTP ${status} Error`);
    if (error.status) {
      (sentryError as any).status = error.status;
    }

    // Отправляем в Sentry
    Sentry.captureException(sentryError, {
      tags,
      extra,
      level: status >= 500 ? 'error' : 'warning',
    });

    // Принудительная отправка в Sentry немедленно
    Sentry.flush(2000).catch((err) => {
      console.error('[Sentry] Ошибка при отправке на сервер:', err);
    });
  } catch (sentryError) {
    // Игнорируем ошибки при отправке в Sentry
    console.error('[Sentry] Failed to send error:', sentryError);
  }
}

/**
 * Устанавливает информацию о пользователе в Sentry
 */
export function setUserContext(user: {
  id?: string;
  email?: string;
  phone?: string;
  role?: string;
  [key: string]: any;
}): void {
  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      phone: user.phone,
      role: user.role,
    });
  } catch (error) {
    console.error('[Sentry] Failed to set user context:', error);
  }
}

/**
 * Очищает информацию о пользователе из Sentry
 */
export function clearUserContext(): void {
  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('[Sentry] Failed to clear user context:', error);
  }
}
